import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';
import { EmptyState, Tooltip } from '../Common/ReusableUI';
import { TableSkeleton } from '../Common/SkeletonLoaders';
import {
  Wrench, Building, AlertTriangle, CheckCircle, Clock, Calendar,
  Search, Plus, Edit, Trash2, ChevronDown, ClipboardList, Settings,
  MapPin, Shield, RefreshCw, FileText, X, ChevronRight, Activity,
  ThermometerSun, Zap, Droplets, HardHat, Cog, Eye, BarChart3,
  Camera, List, LayoutGrid, User
} from 'lucide-react';

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const CATEGORIES = ['Plumbing', 'Electrical', 'HVAC', 'Structural', 'Equipment', 'Safety', 'General'];
const STATUSES = ['Open', 'Assigned', 'In Progress', 'Completed', 'Closed'];
const ASSET_CONDITIONS = ['Good', 'Fair', 'Poor', 'Out of Service'];

const ROOMS = [
  'Room 101 - Banksia Wing', 'Room 102 - Banksia Wing', 'Room 103 - Banksia Wing',
  'Room 104 - Banksia Wing', 'Room 105 - Banksia Wing',
  'Room 201 - Wattle Wing', 'Room 202 - Wattle Wing', 'Room 203 - Wattle Wing',
  'Room 204 - Wattle Wing', 'Room 205 - Wattle Wing',
  'Room 301 - Grevillea Wing', 'Room 302 - Grevillea Wing',
  'Common Area - Dining Hall', 'Common Area - Activity Room', 'Common Area - Garden',
  'Common Area - Reception', 'Nurses Station - Banksia', 'Nurses Station - Wattle',
  'Kitchen', 'Laundry', 'Plant Room', 'External - Car Park', 'External - Main Entry'
];

const TECHNICIANS = [
  'Wayne Mitchell', 'Craig Nguyen', 'Trevor Hoffman', 'Darren Singh', 'Brett Kelly'
];

const STAFF_NAMES = [
  'Sarah Wilson (RN)', 'Emma Thompson (EN)', 'Michael Chen (Care Worker)',
  'Lisa Park (EN)', 'David Rodriguez (RN)', 'Karen O\'Brien (Care Worker)',
  'James Liu (AIN)', 'Angela Morris (Facility Manager)'
];

const initialRequests = [
  { id: 'MR-001', title: 'Leaking tap in ensuite', description: 'Hot water tap dripping constantly in ensuite bathroom. Water pooling on floor — slip hazard.', location: 'Room 101 - Banksia Wing', priority: 'High', category: 'Plumbing', status: 'In Progress', assignedTo: 'Wayne Mitchell', submittedBy: 'Sarah Wilson (RN)', dateSubmitted: '2026-04-02', dateUpdated: '2026-04-05', notes: 'Parts ordered, plumber returning Thursday.', attachments: [] },
  { id: 'MR-002', title: 'Power point not working', description: 'Double GPO beside bed not supplying power. Resident unable to charge electric wheelchair.', location: 'Room 203 - Wattle Wing', priority: 'High', category: 'Electrical', status: 'Assigned', assignedTo: 'Craig Nguyen', submittedBy: 'Emma Thompson (EN)', dateSubmitted: '2026-04-06', dateUpdated: '2026-04-06', notes: '', attachments: [] },
  { id: 'MR-003', title: 'Air conditioning not cooling', description: 'Split system blowing warm air only. Room temperature reading 29°C.', location: 'Room 302 - Grevillea Wing', priority: 'Critical', category: 'HVAC', status: 'Open', assignedTo: '', submittedBy: 'Michael Chen (Care Worker)', dateSubmitted: '2026-04-09', dateUpdated: '2026-04-09', notes: '', attachments: [] },
  { id: 'MR-004', title: 'Cracked floor tile', description: 'Large crack in ceramic floor tile near doorway. Trip hazard for residents using walkers.', location: 'Common Area - Dining Hall', priority: 'Medium', category: 'Structural', status: 'Completed', assignedTo: 'Trevor Hoffman', submittedBy: 'Angela Morris (Facility Manager)', dateSubmitted: '2026-03-20', dateUpdated: '2026-04-01', notes: 'Tile replaced and grouted. Area cleared.', attachments: [] },
  { id: 'MR-005', title: 'Hoist not lowering smoothly', description: 'Ceiling hoist motor stuttering during descent. Making grinding noise.', location: 'Room 104 - Banksia Wing', priority: 'Critical', category: 'Equipment', status: 'In Progress', assignedTo: 'Darren Singh', submittedBy: 'Lisa Park (EN)', dateSubmitted: '2026-04-07', dateUpdated: '2026-04-08', notes: 'Motor assembly being replaced. Room hoist out of service — portable hoist allocated.', attachments: [] },
  { id: 'MR-006', title: 'Emergency exit sign flickering', description: 'Exit sign above fire door B2 intermittently going dark.', location: 'Common Area - Activity Room', priority: 'High', category: 'Safety', status: 'Assigned', assignedTo: 'Craig Nguyen', submittedBy: 'David Rodriguez (RN)', dateSubmitted: '2026-04-08', dateUpdated: '2026-04-08', notes: '', attachments: [] },
  { id: 'MR-007', title: 'Window latch broken', description: 'Latch mechanism snapped. Window cannot be secured closed.', location: 'Room 201 - Wattle Wing', priority: 'Medium', category: 'General', status: 'Open', assignedTo: '', submittedBy: 'Karen O\'Brien (Care Worker)', dateSubmitted: '2026-04-09', dateUpdated: '2026-04-09', notes: '', attachments: [] },
  { id: 'MR-008', title: 'Hot water temperature too high', description: 'Water at basin measured at 52°C. Exceeds 45°C maximum for resident areas.', location: 'Room 105 - Banksia Wing', priority: 'Critical', category: 'Plumbing', status: 'Completed', assignedTo: 'Wayne Mitchell', submittedBy: 'Sarah Wilson (RN)', dateSubmitted: '2026-03-28', dateUpdated: '2026-03-29', notes: 'Tempering valve adjusted and tested. Now reading 42°C.', attachments: [] },
  { id: 'MR-009', title: 'Garden path uneven', description: 'Concrete path section lifted by tree roots. Wheelchair and walker hazard.', location: 'Common Area - Garden', priority: 'Medium', category: 'Structural', status: 'Assigned', assignedTo: 'Trevor Hoffman', submittedBy: 'James Liu (AIN)', dateSubmitted: '2026-04-05', dateUpdated: '2026-04-07', notes: 'Contractor booked for 14 April.', attachments: [] },
  { id: 'MR-010', title: 'Nurse call bell not sounding', description: 'Call bell pressed but no alert at nurses station. Resident unable to summon help.', location: 'Room 202 - Wattle Wing', priority: 'Critical', category: 'Electrical', status: 'Completed', assignedTo: 'Craig Nguyen', submittedBy: 'Emma Thompson (EN)', dateSubmitted: '2026-04-03', dateUpdated: '2026-04-04', notes: 'Faulty relay board replaced. Full system test passed.', attachments: [] },
  { id: 'MR-011', title: 'Dishwasher leaking', description: 'Commercial dishwasher leaking from door seal during cycle.', location: 'Kitchen', priority: 'High', category: 'Equipment', status: 'In Progress', assignedTo: 'Darren Singh', submittedBy: 'Angela Morris (Facility Manager)', dateSubmitted: '2026-04-08', dateUpdated: '2026-04-09', notes: 'Seal ordered from supplier. ETA 2 days.', attachments: [] },
  { id: 'MR-012', title: 'Smoke detector beeping', description: 'Intermittent beep from ceiling detector — likely low battery.', location: 'Room 103 - Banksia Wing', priority: 'High', category: 'Safety', status: 'Completed', assignedTo: 'Brett Kelly', submittedBy: 'Michael Chen (Care Worker)', dateSubmitted: '2026-04-01', dateUpdated: '2026-04-01', notes: 'Battery replaced and unit tested.', attachments: [] }
];

const initialAssets = [
  { id: 'AST-001', name: 'Ceiling Hoist Unit A', type: 'Medical Device', serialNumber: 'HU-2021-4401', location: 'Room 101 - Banksia Wing', purchaseDate: '2021-06-15', warrantyExpiry: '2026-06-15', lastService: '2026-02-10', nextServiceDue: '2026-08-10', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-002', name: 'Ceiling Hoist Unit B', type: 'Medical Device', serialNumber: 'HU-2021-4402', location: 'Room 104 - Banksia Wing', purchaseDate: '2021-06-15', warrantyExpiry: '2026-06-15', lastService: '2026-01-20', nextServiceDue: '2026-07-20', calibrationStatus: 'Current', condition: 'Poor' },
  { id: 'AST-003', name: 'Electric Hi-Lo Bed', type: 'Equipment', serialNumber: 'BED-2022-7810', location: 'Room 201 - Wattle Wing', purchaseDate: '2022-03-01', warrantyExpiry: '2027-03-01', lastService: '2026-03-15', nextServiceDue: '2026-09-15', calibrationStatus: 'N/A', condition: 'Good' },
  { id: 'AST-004', name: 'Electric Hi-Lo Bed', type: 'Equipment', serialNumber: 'BED-2022-7811', location: 'Room 202 - Wattle Wing', purchaseDate: '2022-03-01', warrantyExpiry: '2027-03-01', lastService: '2026-03-15', nextServiceDue: '2026-09-15', calibrationStatus: 'N/A', condition: 'Good' },
  { id: 'AST-005', name: 'Portable Sling Hoist', type: 'Medical Device', serialNumber: 'PH-2020-3350', location: 'Nurses Station - Banksia', purchaseDate: '2020-11-20', warrantyExpiry: '2025-11-20', lastService: '2026-01-05', nextServiceDue: '2026-07-05', calibrationStatus: 'Current', condition: 'Fair' },
  { id: 'AST-006', name: 'Wheelchair — Standard', type: 'Equipment', serialNumber: 'WC-2023-0091', location: 'Room 103 - Banksia Wing', purchaseDate: '2023-01-10', warrantyExpiry: '2028-01-10', lastService: '2026-03-01', nextServiceDue: '2026-09-01', calibrationStatus: 'N/A', condition: 'Good' },
  { id: 'AST-007', name: 'Wheelchair — Bariatric', type: 'Equipment', serialNumber: 'WC-2023-0105', location: 'Room 302 - Grevillea Wing', purchaseDate: '2023-05-22', warrantyExpiry: '2028-05-22', lastService: '2026-02-20', nextServiceDue: '2026-08-20', calibrationStatus: 'N/A', condition: 'Good' },
  { id: 'AST-008', name: 'Vital Signs Monitor', type: 'Medical Device', serialNumber: 'VS-2022-1120', location: 'Nurses Station - Wattle', purchaseDate: '2022-08-14', warrantyExpiry: '2027-08-14', lastService: '2026-02-28', nextServiceDue: '2026-08-28', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-009', name: 'Oxygen Concentrator', type: 'Medical Device', serialNumber: 'OX-2021-5567', location: 'Room 105 - Banksia Wing', purchaseDate: '2021-09-03', warrantyExpiry: '2026-09-03', lastService: '2026-03-10', nextServiceDue: '2026-06-10', calibrationStatus: 'Current', condition: 'Fair' },
  { id: 'AST-010', name: 'Suction Machine', type: 'Medical Device', serialNumber: 'SM-2023-2240', location: 'Nurses Station - Banksia', purchaseDate: '2023-02-17', warrantyExpiry: '2028-02-17', lastService: '2026-01-25', nextServiceDue: '2026-07-25', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-011', name: 'Commercial Dishwasher', type: 'Equipment', serialNumber: 'DW-2020-8800', location: 'Kitchen', purchaseDate: '2020-04-10', warrantyExpiry: '2025-04-10', lastService: '2026-02-15', nextServiceDue: '2026-08-15', calibrationStatus: 'N/A', condition: 'Fair' },
  { id: 'AST-012', name: 'Industrial Washing Machine', type: 'Equipment', serialNumber: 'WM-2021-6620', location: 'Laundry', purchaseDate: '2021-01-28', warrantyExpiry: '2026-01-28', lastService: '2026-03-20', nextServiceDue: '2026-09-20', calibrationStatus: 'N/A', condition: 'Good' },
  { id: 'AST-013', name: 'Fire Panel — Main', type: 'Safety System', serialNumber: 'FP-2019-1001', location: 'Common Area - Reception', purchaseDate: '2019-07-01', warrantyExpiry: '2029-07-01', lastService: '2026-03-01', nextServiceDue: '2026-06-01', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-014', name: 'Backup Generator', type: 'Infrastructure', serialNumber: 'GEN-2020-3000', location: 'Plant Room', purchaseDate: '2020-02-14', warrantyExpiry: '2030-02-14', lastService: '2026-01-15', nextServiceDue: '2026-04-15', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-015', name: 'Passenger Lift', type: 'Infrastructure', serialNumber: 'LFT-2018-0501', location: 'Common Area - Reception', purchaseDate: '2018-11-30', warrantyExpiry: '2028-11-30', lastService: '2026-02-01', nextServiceDue: '2026-05-01', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-016', name: 'Pressure Mattress', type: 'Medical Device', serialNumber: 'PM-2024-9910', location: 'Room 204 - Wattle Wing', purchaseDate: '2024-08-01', warrantyExpiry: '2029-08-01', lastService: '2026-02-20', nextServiceDue: '2026-08-20', calibrationStatus: 'N/A', condition: 'Good' },
  { id: 'AST-017', name: 'Blood Glucose Monitor', type: 'Medical Device', serialNumber: 'BG-2024-4455', location: 'Nurses Station - Wattle', purchaseDate: '2024-03-15', warrantyExpiry: '2029-03-15', lastService: '2026-03-15', nextServiceDue: '2026-06-15', calibrationStatus: 'Current', condition: 'Good' },
  { id: 'AST-018', name: 'Shower Commode Chair', type: 'Equipment', serialNumber: 'SC-2022-1180', location: 'Room 301 - Grevillea Wing', purchaseDate: '2022-12-05', warrantyExpiry: '2027-12-05', lastService: '2026-03-01', nextServiceDue: '2026-09-01', calibrationStatus: 'N/A', condition: 'Fair' }
];

const initialPreventiveSchedule = [
  { id: 'PM-001', task: 'Fire system quarterly inspection', equipment: 'Fire Panel — Main', frequency: 'Quarterly', lastCompleted: '2026-03-01', nextDue: '2026-06-01', assignedTo: 'Brett Kelly', status: 'Upcoming', category: 'Safety' },
  { id: 'PM-002', task: 'Generator load test', equipment: 'Backup Generator', frequency: 'Monthly', lastCompleted: '2026-03-15', nextDue: '2026-04-15', assignedTo: 'Wayne Mitchell', status: 'Upcoming', category: 'Infrastructure' },
  { id: 'PM-003', task: 'Lift annual inspection', equipment: 'Passenger Lift', frequency: 'Annually', lastCompleted: '2025-05-01', nextDue: '2026-05-01', assignedTo: 'External — Schindler', status: 'Upcoming', category: 'Infrastructure' },
  { id: 'PM-004', task: 'Hoist 6-monthly service', equipment: 'Ceiling Hoist Unit A', frequency: '6-Monthly', lastCompleted: '2026-02-10', nextDue: '2026-08-10', assignedTo: 'Darren Singh', status: 'Upcoming', category: 'Equipment' },
  { id: 'PM-005', task: 'Hoist 6-monthly service', equipment: 'Ceiling Hoist Unit B', frequency: '6-Monthly', lastCompleted: '2026-01-20', nextDue: '2026-07-20', assignedTo: 'Darren Singh', status: 'Upcoming', category: 'Equipment' },
  { id: 'PM-006', task: 'Hot water tempering valve check', equipment: 'All Ensuites', frequency: 'Monthly', lastCompleted: '2026-03-28', nextDue: '2026-04-28', assignedTo: 'Wayne Mitchell', status: 'Upcoming', category: 'Plumbing' },
  { id: 'PM-007', task: 'HVAC filter replacement', equipment: 'Split Systems — All Wings', frequency: 'Quarterly', lastCompleted: '2026-01-15', nextDue: '2026-04-15', assignedTo: 'Craig Nguyen', status: 'Upcoming', category: 'HVAC' },
  { id: 'PM-008', task: 'Emergency lighting test', equipment: 'All Emergency Lights', frequency: 'Monthly', lastCompleted: '2026-03-05', nextDue: '2026-04-05', assignedTo: 'Brett Kelly', status: 'Overdue', category: 'Safety' },
  { id: 'PM-009', task: 'RCD safety switch test', equipment: 'Switchboard — Main', frequency: 'Quarterly', lastCompleted: '2026-01-10', nextDue: '2026-04-10', assignedTo: 'Craig Nguyen', status: 'Upcoming', category: 'Electrical' },
  { id: 'PM-010', task: 'Oxygen concentrator calibration', equipment: 'Oxygen Concentrator', frequency: '6-Monthly', lastCompleted: '2026-03-10', nextDue: '2026-09-10', assignedTo: 'External — BOC Healthcare', status: 'Upcoming', category: 'Medical' },
  { id: 'PM-011', task: 'Pest control treatment', equipment: 'Entire Facility', frequency: 'Monthly', lastCompleted: '2026-03-22', nextDue: '2026-04-22', assignedTo: 'External — Rentokil', status: 'Upcoming', category: 'General' },
  { id: 'PM-012', task: 'Fire extinguisher 6-monthly check', equipment: 'All Extinguishers (24 units)', frequency: '6-Monthly', lastCompleted: '2025-10-15', nextDue: '2026-04-15', assignedTo: 'External — Wormald', status: 'Upcoming', category: 'Safety' },
  { id: 'PM-013', task: 'Backflow prevention valve test', equipment: 'Main Water Supply', frequency: 'Annually', lastCompleted: '2025-06-20', nextDue: '2026-06-20', assignedTo: 'External — Licensed Plumber', status: 'Upcoming', category: 'Plumbing' },
  { id: 'PM-014', task: 'Legionella water testing', equipment: 'All Hot Water Systems', frequency: 'Monthly', lastCompleted: '2026-03-08', nextDue: '2026-04-08', assignedTo: 'Wayne Mitchell', status: 'Overdue', category: 'Safety' }
];

const priorityConfig = {
  Critical: { color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', icon: AlertTriangle, tip: 'Immediate attention required' },
  High: { color: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500', icon: AlertTriangle, tip: 'Address within 24 hours' },
  Medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500', icon: Clock, tip: 'Schedule within 3 days' },
  Low: { color: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-500', icon: Clock, tip: 'Non-urgent, schedule when convenient' }
};

const statusConfig = {
  Open: { color: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle, tip: 'Awaiting triage and assignment' },
  Assigned: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: ClipboardList, tip: 'Assigned to a technician' },
  'In Progress': { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: RefreshCw, tip: 'Work underway' },
  Completed: { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle, tip: 'Work finished and verified' },
  Closed: { color: 'bg-gray-50 text-gray-600 border-gray-200', icon: Shield, tip: 'Closed — no further action' }
};

const conditionConfig = {
  Good: 'bg-green-100 text-green-800',
  Fair: 'bg-yellow-100 text-yellow-800',
  Poor: 'bg-red-100 text-red-800',
  'Out of Service': 'bg-gray-200 text-gray-800'
};

const categoryIcons = {
  Plumbing: Droplets, Electrical: Zap, HVAC: ThermometerSun, Structural: Building,
  Equipment: Cog, Safety: Shield, General: Wrench, Infrastructure: HardHat, Medical: Activity
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(dateStr) {
  const now = new Date('2026-04-10T00:00:00');
  const target = new Date(dateStr + 'T00:00:00');
  return Math.ceil((target - now) / 86400000);
}

function getTimeAgo(dateStr) {
  const now = new Date('2026-04-10T12:00:00');
  const created = new Date(dateStr + 'T08:00:00');
  const hours = Math.max(0, Math.floor((now - created) / 3600000));
  if (hours < 1) return { text: 'Just now', hours: 0 };
  if (hours < 24) return { text: `${hours}h ago`, hours };
  const days = Math.floor(hours / 24);
  return { text: `${days}d ago`, hours };
}

function getSLAColor(hours) {
  if (hours < 4) return 'bg-green-100 text-green-700 border-green-200';
  if (hours < 24) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
}

const Maintenance = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');
  const [viewMode, setViewMode] = useState('list');
  const [requests, setRequests] = useState(initialRequests);
  const [assets] = useState(initialAssets);
  const [preventiveSchedule, setPreventiveSchedule] = useState(initialPreventiveSchedule);

  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState('all');
  const [assetConditionFilter, setAssetConditionFilter] = useState('all');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [scheduleFilter, setScheduleFilter] = useState('all');

  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({});
  const [newRequest, setNewRequest] = useState({
    title: '', description: '', location: '', priority: 'Medium', category: '', submittedBy: '', attachments: []
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const validateField = useCallback((field, value) => {
    switch (field) {
      case 'title':
        if (!value || !value.trim()) return 'Title is required';
        if (value.trim().length < 5) return 'Title must be at least 5 characters';
        return '';
      case 'description':
        if (!value || !value.trim()) return 'Description is required';
        return '';
      case 'category':
        if (!value) return 'Please select a category';
        return '';
      default:
        return '';
    }
  }, []);

  const handleBlur = useCallback((field) => {
    setFormTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, newRequest[field]);
    setFormErrors(prev => ({ ...prev, [field]: error }));
  }, [newRequest, validateField]);

  const handleFieldChange = useCallback((field, value) => {
    setNewRequest(prev => ({ ...prev, [field]: value }));
    if (formTouched[field]) {
      const error = validateField(field, value);
      setFormErrors(prev => ({ ...prev, [field]: error }));
    }
  }, [formTouched, validateField]);

  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setNewRequest(prev => ({
          ...prev,
          attachments: [...prev.attachments, { name: file.name, data: ev.target.result }]
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, []);

  const removeAttachment = useCallback((index) => {
    setNewRequest(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  }, []);

  const tabs = [
    { id: 'requests', label: 'Requests', icon: ClipboardList },
    { id: 'rooms', label: 'Room History', icon: Building },
    { id: 'preventive', label: 'Preventive Schedule', icon: Calendar },
    { id: 'assets', label: 'Asset Register', icon: Settings }
  ];

  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false;
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return r.title.toLowerCase().includes(q) || r.location.toLowerCase().includes(q) || r.id.toLowerCase().includes(q) || r.description.toLowerCase().includes(q);
      }
      return true;
    });
  }, [requests, statusFilter, priorityFilter, categoryFilter, searchQuery]);

  const roomRequests = useMemo(() => {
    if (!selectedRoom) return [];
    return requests.filter(r => r.location === selectedRoom);
  }, [requests, selectedRoom]);

  const roomStats = useMemo(() => {
    if (!selectedRoom) return null;
    const items = requests.filter(r => r.location === selectedRoom);
    return {
      total: items.length,
      open: items.filter(r => ['Open', 'Assigned', 'In Progress'].includes(r.status)).length,
      completed: items.filter(r => r.status === 'Completed' || r.status === 'Closed').length,
      critical: items.filter(r => r.priority === 'Critical').length
    };
  }, [requests, selectedRoom]);

  const filteredAssets = useMemo(() => {
    return assets.filter(a => {
      if (assetTypeFilter !== 'all' && a.type !== assetTypeFilter) return false;
      if (assetConditionFilter !== 'all' && a.condition !== assetConditionFilter) return false;
      if (assetSearch) {
        const q = assetSearch.toLowerCase();
        return a.name.toLowerCase().includes(q) || a.serialNumber.toLowerCase().includes(q) || a.location.toLowerCase().includes(q) || a.id.toLowerCase().includes(q);
      }
      return true;
    });
  }, [assets, assetTypeFilter, assetConditionFilter, assetSearch]);

  const filteredSchedule = useMemo(() => {
    if (scheduleFilter === 'all') return preventiveSchedule;
    if (scheduleFilter === 'overdue') return preventiveSchedule.filter(s => s.status === 'Overdue');
    if (scheduleFilter === 'upcoming') return preventiveSchedule.filter(s => s.status === 'Upcoming' && daysUntil(s.nextDue) <= 30);
    if (scheduleFilter === 'completed') return preventiveSchedule.filter(s => s.status === 'Completed');
    return preventiveSchedule;
  }, [preventiveSchedule, scheduleFilter]);

  const summaryStats = useMemo(() => ({
    total: requests.length,
    open: requests.filter(r => r.status === 'Open').length,
    inProgress: requests.filter(r => r.status === 'In Progress' || r.status === 'Assigned').length,
    completed: requests.filter(r => r.status === 'Completed').length,
    critical: requests.filter(r => r.priority === 'Critical' && r.status !== 'Completed' && r.status !== 'Closed').length,
    overduePM: preventiveSchedule.filter(s => s.status === 'Overdue').length
  }), [requests, preventiveSchedule]);

  const assetTypes = useMemo(() => [...new Set(assets.map(a => a.type))], [assets]);

  const handleSubmitRequest = () => {
    const errors = {
      title: validateField('title', newRequest.title),
      description: validateField('description', newRequest.description),
      category: validateField('category', newRequest.category),
    };
    setFormErrors(errors);
    setFormTouched({ title: true, description: true, category: true });
    if (Object.values(errors).some(e => e)) return;
    if (!newRequest.location || !newRequest.submittedBy) return;

    const id = `MR-${String(requests.length + 1).padStart(3, '0')}`;
    const today = '2026-04-10';
    setRequests(prev => [...prev, {
      ...newRequest, id, status: 'Open', assignedTo: '', dateSubmitted: today, dateUpdated: today, notes: ''
    }]);
    setNewRequest({ title: '', description: '', location: '', priority: 'Medium', category: '', submittedBy: '', attachments: [] });
    setFormErrors({});
    setFormTouched({});
    setShowNewRequestForm(false);
    showToast('Maintenance request created successfully', 'success');
  };

  const handleUpdateStatus = (id, newStatus) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus, dateUpdated: '2026-04-10' } : r));
    showToast(`${id} status updated to ${newStatus}`, 'success');
  };

  const handleAssignTechnician = (id, technician) => {
    setRequests(prev => prev.map(r =>
      r.id === id ? { ...r, assignedTo: technician, status: r.status === 'Open' ? 'Assigned' : r.status, dateUpdated: '2026-04-10' } : r
    ));
    showToast(`${id} assigned to ${technician}`, 'success');
  };

  const handleMarkComplete = (id) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'Completed', dateUpdated: '2026-04-10' } : r));
    showToast(`${id} marked as completed`, 'success');
  };

  const handleDeleteRequest = (id) => {
    setRequests(prev => prev.filter(r => r.id !== id));
    setExpandedRequest(null);
    showToast(`Request ${id} deleted`, 'warning');
  };

  const handleMarkPMComplete = (pmId) => {
    setPreventiveSchedule(prev => prev.map(pm =>
      pm.id === pmId ? { ...pm, status: 'Completed', lastCompleted: '2026-04-10' } : pm
    ));
    showToast(`Preventive maintenance ${pmId} completed`, 'success');
  };

  const PriorityBadge = ({ priority }) => {
    const cfg = priorityConfig[priority];
    return (
      <Tooltip content={`${priority} priority — ${cfg.tip}`}>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          {priority}
        </span>
      </Tooltip>
    );
  };

  const StatusBadge = ({ status }) => {
    const cfg = statusConfig[status];
    if (!cfg) return null;
    const Icon = cfg.icon;
    return (
      <Tooltip content={cfg.tip}>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
          <Icon size={12} />
          {status}
        </span>
      </Tooltip>
    );
  };

  const SLABadge = ({ dateStr, priority }) => {
    const { text, hours } = getTimeAgo(dateStr);
    const color = getSLAColor(hours);
    const urgency = priority === 'Critical'
      ? (hours < 4 ? 'Within SLA' : hours < 24 ? 'Approaching SLA limit' : 'SLA breached')
      : (hours < 24 ? 'Standard response' : 'Extended wait');
    return (
      <Tooltip content={`Created ${text} · ${urgency} (green < 4h, amber 4–24h, red > 24h)`}>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
          <Clock size={10} />
          {text}
        </span>
      </Tooltip>
    );
  };

  const renderSummaryCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {[
        { label: 'Total Requests', value: summaryStats.total, icon: ClipboardList, color: 'purple' },
        { label: 'Open', value: summaryStats.open, icon: AlertTriangle, color: 'red' },
        { label: 'In Progress', value: summaryStats.inProgress, icon: RefreshCw, color: 'amber' },
        { label: 'Completed', value: summaryStats.completed, icon: CheckCircle, color: 'green' },
        { label: 'Critical Active', value: summaryStats.critical, icon: Shield, color: 'red' },
        { label: 'Overdue PM', value: summaryStats.overduePM, icon: Calendar, color: 'orange' }
      ].map(card => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg bg-${card.color}-50`}>
                <Icon size={18} className={`text-${card.color}-600`} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
          </div>
        );
      })}
    </div>
  );

  const renderNewRequestForm = () => {
    const fieldBorder = (field) =>
      formTouched[field] && formErrors[field]
        ? 'border-red-400 ring-1 ring-red-200 focus:ring-red-500'
        : 'border-gray-300 focus:ring-purple-500';

    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-700 rounded-t-2xl">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Plus size={20} /> New Maintenance Request</h3>
            <button onClick={() => { setShowNewRequestForm(false); setFormErrors({}); setFormTouched({}); }} className="text-white/80 hover:text-white"><X size={20} /></button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={newRequest.title}
                onChange={e => handleFieldChange('title', e.target.value)}
                onBlur={() => handleBlur('title')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${fieldBorder('title')}`}
                placeholder="Brief description of the issue (min 5 characters)"
              />
              {formTouched.title && formErrors.title && (
                <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                rows={3}
                value={newRequest.description}
                onChange={e => handleFieldChange('description', e.target.value)}
                onBlur={() => handleBlur('description')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${fieldBorder('description')}`}
                placeholder="Detailed description of the maintenance issue"
              />
              {formTouched.description && formErrors.description && (
                <p className="text-xs text-red-500 mt-1">{formErrors.description}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attach Photos</label>
              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-colors">
                <Camera size={24} className="text-gray-400 mb-1" />
                <span className="text-xs text-gray-500">Click to attach photos</span>
                <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" />
              </label>
              {newRequest.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newRequest.attachments.map((att, i) => (
                    <div key={i} className="relative group">
                      <img src={att.data} alt={att.name} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                      <button
                        onClick={() => removeAttachment(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                      <p className="text-xs text-gray-400 truncate w-16 mt-0.5">{att.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <select value={newRequest.location} onChange={e => setNewRequest(p => ({ ...p, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white">
                  <option value="">Select location</option>
                  {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={newRequest.category}
                  onChange={e => handleFieldChange('category', e.target.value)}
                  onBlur={() => handleBlur('category')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm bg-white ${fieldBorder('category')}`}
                >
                  <option value="">Select category</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {formTouched.category && formErrors.category && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.category}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select value={newRequest.priority} onChange={e => setNewRequest(p => ({ ...p, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white">
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted By *</label>
                <select value={newRequest.submittedBy} onChange={e => setNewRequest(p => ({ ...p, submittedBy: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white">
                  <option value="">Select staff member</option>
                  {STAFF_NAMES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setShowNewRequestForm(false); setFormErrors({}); setFormTouched({}); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSubmitRequest}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Plus size={16} /> Submit Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKanbanBoard = () => {
    const columns = [
      { status: 'Open', borderColor: 'border-red-400', headerBg: 'bg-red-50', headerText: 'text-red-700', countBg: 'bg-red-100 text-red-700' },
      { status: 'Assigned', borderColor: 'border-blue-400', headerBg: 'bg-blue-50', headerText: 'text-blue-700', countBg: 'bg-blue-100 text-blue-700' },
      { status: 'In Progress', borderColor: 'border-amber-400', headerBg: 'bg-amber-50', headerText: 'text-amber-700', countBg: 'bg-amber-100 text-amber-700' },
      { status: 'Completed', borderColor: 'border-green-400', headerBg: 'bg-green-50', headerText: 'text-green-700', countBg: 'bg-green-100 text-green-700' },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map(col => {
          const columnRequests = filteredRequests.filter(r => r.status === col.status);
          return (
            <div key={col.status} className="bg-gray-50 rounded-xl p-3 min-h-[320px]">
              <div className={`flex items-center justify-between mb-3 pb-2 border-b-2 ${col.borderColor}`}>
                <h3 className={`font-semibold text-sm ${col.headerText}`}>{col.status}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${col.countBg}`}>{columnRequests.length}</span>
              </div>
              <div className="space-y-2">
                {columnRequests.map(r => {
                  const CatIcon = categoryIcons[r.category] || Wrench;
                  return (
                    <div
                      key={r.id}
                      className={`bg-white rounded-lg border ${r.priority === 'Critical' ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'} p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
                      onClick={() => setExpandedRequest(expandedRequest === r.id ? null : r.id)}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-mono text-gray-400">{r.id}</span>
                        <PriorityBadge priority={r.priority} />
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1.5 line-clamp-2">{r.title}</h4>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                        <MapPin size={10} /> <span className="truncate">{r.location.split(' - ')[0]}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <SLABadge dateStr={r.dateSubmitted} priority={r.priority} />
                        {r.assignedTo && (
                          <Tooltip content={`Assigned to ${r.assignedTo}`}>
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <User size={10} /> {r.assignedTo.split(' ')[0]}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
                {columnRequests.length === 0 && (
                  <div className="text-center py-8 text-gray-300">
                    <ClipboardList size={24} className="mx-auto mb-1 opacity-40" />
                    <p className="text-xs">No tickets</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRequestsTab = () => (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex flex-1 items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search requests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
            <option value="all">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
            <option value="all">All Priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 hidden lg:block">
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 ml-1">
            <button onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-400 hover:text-gray-600'}`}
              title="List View">
              <List size={16} />
            </button>
            <button onClick={() => setViewMode('board')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'board' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-400 hover:text-gray-600'}`}
              title="Board View">
              <LayoutGrid size={16} />
            </button>
          </div>
        </div>
        <button onClick={() => setShowNewRequestForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm">
          <Plus size={16} /> New Request
        </button>
      </div>

      <div className="text-xs text-gray-500 mb-3">{filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found</div>

      {viewMode === 'board' ? renderKanbanBoard() : (
        <div className="space-y-2">
          {filteredRequests.map(r => {
            const CatIcon = categoryIcons[r.category] || Wrench;
            const isExpanded = expandedRequest === r.id;
            return (
              <div key={r.id} className={`bg-white rounded-xl border ${r.priority === 'Critical' && r.status !== 'Completed' && r.status !== 'Closed' ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'} shadow-sm hover:shadow-md transition-all`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpandedRequest(isExpanded ? null : r.id)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${r.priority === 'Critical' ? 'bg-red-50' : 'bg-purple-50'} mt-0.5`}>
                        <CatIcon size={18} className={r.priority === 'Critical' ? 'text-red-600' : 'text-purple-600'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono text-gray-400">{r.id}</span>
                          <PriorityBadge priority={r.priority} />
                          <StatusBadge status={r.status} />
                          <SLABadge dateStr={r.dateSubmitted} priority={r.priority} />
                        </div>
                        <h4 className="font-semibold text-gray-900 mt-1 truncate">{r.title}</h4>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><MapPin size={12} /> {r.location}</span>
                          <span className="flex items-center gap-1"><Clock size={12} /> {formatDate(r.dateSubmitted)}</span>
                          {r.assignedTo && <span className="flex items-center gap-1"><Wrench size={12} /> {r.assignedTo}</span>}
                        </div>
                      </div>
                    </div>
                    <ChevronDown size={18} className={`text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                    <p className="text-sm text-gray-700">{r.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-gray-400">Submitted by</span><p className="font-medium text-gray-700">{r.submittedBy}</p></div>
                      <div><span className="text-gray-400">Category</span><p className="font-medium text-gray-700">{r.category}</p></div>
                      <div><span className="text-gray-400">Last Updated</span><p className="font-medium text-gray-700">{formatDate(r.dateUpdated)}</p></div>
                      <div><span className="text-gray-400">Assigned To</span><p className="font-medium text-gray-700">{r.assignedTo || 'Unassigned'}</p></div>
                    </div>
                    {r.notes && (
                      <div className="bg-gray-50 rounded-lg p-3 text-xs">
                        <span className="font-medium text-gray-500 flex items-center gap-1 mb-1"><FileText size={12} /> Notes</span>
                        <p className="text-gray-700">{r.notes}</p>
                      </div>
                    )}
                    {r.attachments && r.attachments.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-1.5"><Camera size={12} /> Photos ({r.attachments.length})</span>
                        <div className="flex flex-wrap gap-2">
                          {r.attachments.map((att, i) => (
                            <img key={i} src={att.data} alt={att.name} className="w-20 h-20 object-cover rounded-lg border border-gray-200 shadow-sm" />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                      {r.status !== 'Completed' && r.status !== 'Closed' && (
                        <>
                          <select
                            defaultValue=""
                            onChange={e => { if (e.target.value) { handleAssignTechnician(r.id, e.target.value); e.target.value = ''; } }}
                            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                          >
                            <option value="" disabled>Assign to...</option>
                            {TECHNICIANS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select
                            defaultValue=""
                            onChange={e => { if (e.target.value) { handleUpdateStatus(r.id, e.target.value); e.target.value = ''; } }}
                            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                          >
                            <option value="" disabled>Change status...</option>
                            {STATUSES.filter(s => s !== r.status).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <button onClick={() => handleMarkComplete(r.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 border border-green-200">
                            <CheckCircle size={12} /> Complete
                          </button>
                        </>
                      )}
                      <button onClick={() => handleDeleteRequest(r.id)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 border border-red-200 ml-auto">
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filteredRequests.length === 0 && (
            <EmptyState
              icon={Search}
              title="No requests found"
              description="Try adjusting your filters or search query to find maintenance requests."
            />
          )}
        </div>
      )}
    </div>
  );

  const renderRoomHistoryTab = () => (
    <div>
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Room / Area</label>
        <div className="relative max-w-md">
          <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select value={selectedRoom} onChange={e => setSelectedRoom(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent">
            <option value="">Choose a room or area...</option>
            {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {!selectedRoom ? (
        <div className="text-center py-16 text-gray-400">
          <Building size={48} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">Select a room to view its maintenance history</p>
        </div>
      ) : (
        <>
          {roomStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Total Requests', value: roomStats.total, color: 'purple' },
                { label: 'Active', value: roomStats.open, color: 'amber' },
                { label: 'Completed', value: roomStats.completed, color: 'green' },
                { label: 'Critical', value: roomStats.critical, color: 'red' }
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <MapPin size={16} className="text-purple-600" /> {selectedRoom}
          </h3>

          {roomRequests.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
              <CheckCircle size={36} className="mx-auto mb-2 text-green-400 opacity-60" />
              <p className="text-sm text-gray-500">No maintenance history for this location.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {roomRequests.map(r => {
                const CatIcon = categoryIcons[r.category] || Wrench;
                return (
                  <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-purple-50"><CatIcon size={16} className="text-purple-600" /></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono text-gray-400">{r.id}</span>
                          <PriorityBadge priority={r.priority} />
                          <StatusBadge status={r.status} />
                          <SLABadge dateStr={r.dateSubmitted} priority={r.priority} />
                        </div>
                        <h4 className="font-medium text-gray-900 mt-1 text-sm">{r.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span>{formatDate(r.dateSubmitted)}</span>
                          {r.assignedTo && <span>Assigned: {r.assignedTo}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Settings size={14} className="text-purple-600" /> Assets at this Location</h4>
            {assets.filter(a => a.location === selectedRoom).length === 0 ? (
              <p className="text-xs text-gray-400">No registered assets at this location.</p>
            ) : (
              <div className="space-y-2">
                {assets.filter(a => a.location === selectedRoom).map(a => (
                  <div key={a.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{a.name}</p>
                      <p className="text-xs text-gray-400">S/N: {a.serialNumber} · Next service: {formatDate(a.nextServiceDue)}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${conditionConfig[a.condition]}`}>{a.condition}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderPreventiveScheduleTab = () => {
    const overdue = preventiveSchedule.filter(s => s.status === 'Overdue');
    const upcomingMonth = preventiveSchedule.filter(s => s.status === 'Upcoming' && daysUntil(s.nextDue) <= 30 && daysUntil(s.nextDue) >= 0);
    const completedItems = preventiveSchedule.filter(s => s.status === 'Completed');

    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total Scheduled', value: preventiveSchedule.length, color: 'purple' },
            { label: 'Overdue', value: overdue.length, color: 'red' },
            { label: 'Due This Month', value: upcomingMonth.length, color: 'amber' },
            { label: 'Completed', value: completedItems.length, color: 'green' }
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {[
            { id: 'all', label: 'All' },
            { id: 'overdue', label: `Overdue (${overdue.length})` },
            { id: 'upcoming', label: `Due ≤30 Days (${upcomingMonth.length})` },
            { id: 'completed', label: `Completed (${completedItems.length})` }
          ].map(f => (
            <button key={f.id} onClick={() => setScheduleFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${scheduleFilter === f.id ? 'bg-purple-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {filteredSchedule.map(pm => {
            const days = daysUntil(pm.nextDue);
            const isOverdue = pm.status === 'Overdue' || (pm.status !== 'Completed' && days < 0);
            const isDueSoon = !isOverdue && pm.status !== 'Completed' && days <= 7;
            const isCompleted = pm.status === 'Completed';
            const CatIcon = categoryIcons[pm.category] || Calendar;

            return (
              <div key={pm.id} className={`bg-white rounded-xl border ${isOverdue ? 'border-red-300 ring-1 ring-red-100' : isDueSoon ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'} p-4 shadow-sm`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${isOverdue ? 'bg-red-50' : isCompleted ? 'bg-green-50' : 'bg-purple-50'}`}>
                      <CatIcon size={18} className={isOverdue ? 'text-red-600' : isCompleted ? 'text-green-600' : 'text-purple-600'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-gray-400">{pm.id}</span>
                        {isOverdue && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">Overdue</span>}
                        {isDueSoon && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">Due Soon</span>}
                        {isCompleted && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">Completed</span>}
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{pm.frequency}</span>
                      </div>
                      <h4 className="font-medium text-gray-900 mt-1 text-sm">{pm.task}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{pm.equipment}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Clock size={12} /> Last: {formatDate(pm.lastCompleted)}</span>
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}><Calendar size={12} /> Due: {formatDate(pm.nextDue)}</span>
                        <span className="flex items-center gap-1"><Wrench size={12} /> {pm.assignedTo}</span>
                      </div>
                    </div>
                  </div>
                  {!isCompleted && (
                    <button onClick={() => handleMarkPMComplete(pm.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors border border-green-200 whitespace-nowrap">
                      <CheckCircle size={14} /> Mark Complete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filteredSchedule.length === 0 && (
            <EmptyState
              icon={Calendar}
              title="No scheduled items"
              description="No preventive maintenance items match this filter."
            />
          )}
        </div>
      </div>
    );
  };

  const renderAssetRegisterTab = () => (
    <div>
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search assets by name, serial, location..." value={assetSearch} onChange={e => setAssetSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
        </div>
        <select value={assetTypeFilter} onChange={e => setAssetTypeFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
          <option value="all">All Types</option>
          {assetTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={assetConditionFilter} onChange={e => setAssetConditionFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500">
          <option value="all">All Conditions</option>
          {ASSET_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="text-xs text-gray-500 mb-3">{filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''} found</div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Asset', 'Type', 'Serial Number', 'Location', 'Condition', 'Last Service', 'Next Service', 'Calibration', 'Warranty'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredAssets.map(a => {
              const serviceDays = daysUntil(a.nextServiceDue);
              const warrantyDays = daysUntil(a.warrantyExpiry);
              return (
                <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-purple-50"><Settings size={14} className="text-purple-600" /></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.name}</p>
                        <p className="text-xs text-gray-400">{a.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{a.type}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">{a.serialNumber}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <span className="flex items-center gap-1"><MapPin size={12} className="text-gray-400" />{a.location}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${conditionConfig[a.condition]}`}>{a.condition}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(a.lastService)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs font-medium ${serviceDays <= 0 ? 'text-red-600' : serviceDays <= 30 ? 'text-amber-600' : 'text-gray-500'}`}>
                      {formatDate(a.nextServiceDue)}
                      {serviceDays <= 0 && <span className="ml-1 text-red-500">(Overdue)</span>}
                      {serviceDays > 0 && serviceDays <= 30 && <span className="ml-1">({serviceDays}d)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.calibrationStatus === 'Current' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {a.calibrationStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs ${warrantyDays <= 0 ? 'text-red-500 font-medium' : warrantyDays <= 90 ? 'text-amber-600' : 'text-gray-500'}`}>
                      {formatDate(a.warrantyExpiry)}
                      {warrantyDays <= 0 && <span className="ml-1">(Expired)</span>}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredAssets.length === 0 && (
          <EmptyState
            icon={Search}
            title="No assets found"
            description="No assets match your search criteria."
          />
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-purple-800 rounded-2xl p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl"><Wrench size={24} /></div>
            Facility Maintenance
          </h1>
          <p className="text-purple-200 mt-1 text-sm">Loading maintenance data...</p>
        </div>
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-purple-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl"><Wrench size={24} /></div>
              Facility Maintenance
            </h1>
            <p className="text-purple-200 mt-1 text-sm">Manage maintenance requests, preventive scheduling, and asset tracking</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            {summaryStats.critical > 0 && (
              <div className="bg-red-500/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 animate-pulse">
                <AlertTriangle size={14} /> {summaryStats.critical} Critical
              </div>
            )}
            {summaryStats.overduePM > 0 && (
              <div className="bg-amber-500/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5">
                <Clock size={14} /> {summaryStats.overduePM} Overdue PM
              </div>
            )}
          </div>
        </div>
      </div>

      {renderSummaryCards()}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50/50 px-1 pt-1">
          <div className="flex gap-0.5 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-white text-purple-700 border border-gray-200 border-b-white -mb-px shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'
                  }`}>
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-5">
          {activeTab === 'requests' && renderRequestsTab()}
          {activeTab === 'rooms' && renderRoomHistoryTab()}
          {activeTab === 'preventive' && renderPreventiveScheduleTab()}
          {activeTab === 'assets' && renderAssetRegisterTab()}
        </div>
      </div>

      {showNewRequestForm && renderNewRequestForm()}
    </div>
  );
};

export default Maintenance;
